import { Col, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useState } from "react";
import { say } from "../../localization/say.ts";
import type { Reaction } from "./reacting.ts";
import { whoReacted } from "./reaction-names.ts";

function Tip({ reaction, testID }: { reaction: Reaction; testID: string }) {
  const theme = useTheme();
  return (
    <Col
      testID={`${testID}-who`}
      style={{
        position: "absolute",
        bottom: "100%",
        left: 0,
        marginBottom: 6,
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 5,
        borderRadius: 6,
        backgroundColor: theme.color["text.primary"],
        zIndex: 30,
      }}
      {...{ "aria-hidden": true, pointerEvents: "none" as const }}
    >
      <Text style={{ fontSize: 12 }}>{reaction.emoji}</Text>
      <Text
        typeName="caption"
        color="bg.app"
        testID={`${testID}-tip`}
        style={{ whiteSpace: "nowrap" } as never}
      >
        {whoReacted(reaction)}
      </Text>
    </Col>
  );
}

export function Chip({
  reaction,
  onPress,
  onHold,
  onReading,
  testID,
}: {
  reaction: Reaction;
  onPress: () => void;

  onHold?: () => void;

  onReading?: (reading: boolean) => void;
  testID: string;
}) {
  const theme = useTheme();

  const [asking, setAsking] = useState(false);
  const reading = (on: boolean) => {
    setAsking(on);
    onReading?.(on);
  };
  return (
    <Col style={{ position: "relative", overflow: "visible" }}>
      {asking ? <Tip reaction={reaction} testID={testID} /> : null}
      <Tappable
        onHoverIn={() => reading(true)}
        onHoverOut={() => reading(false)}
        onFocus={() => reading(true)}
        onBlur={() => reading(false)}
        onPress={onPress}
        {...(onHold ? { onLongPress: onHold } : {})}
        accessibilityRole="button"
        accessibilityState={{ selected: reaction.mine }}
        accessibilityLabel={whoReacted(reaction)}
        testID={testID}
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 4,
          paddingHorizontal: 8,
          height: 24,
          borderRadius: theme.radius.round,

          borderWidth: 1,
          borderColor: reaction.mine ? theme.color.accent : theme.color["border.default"],
          backgroundColor: theme.color["bg.surface"],
        }}
      >
        <Text style={{ fontSize: 13 }}>{reaction.emoji}</Text>
        <Text typeName="caption" color={reaction.mine ? "accent" : "text.secondary"}>
          {String(reaction.count)}
        </Text>
      </Tappable>
    </Col>
  );
}

export function MoreChip({ many, onPress, testID }: { many: number; onPress: () => void; testID: string }) {
  const theme = useTheme();
  return (
    <Tappable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={say("react.showMore")(many)}
      testID={testID}
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 8,
        height: 24,
        borderRadius: theme.radius.round,
        borderWidth: 1,
        borderColor: theme.color["border.default"],
        backgroundColor: theme.color["bg.surfaceAlt"],
      }}
    >
      <Text typeName="caption" color="text.secondary">
        {say("react.more")(many)}
      </Text>
    </Tappable>
  );
}
